// src/shared/services/tenantAccess.js
//
// CHA-552 — the tenant-ENTRY gate, extracted from App.jsx so it can be
// tested without standing up App's whole dependency graph.
//
// This is deliberately NOT a fourth permission system (CLAUDE.md §8.2).
// It is one predicate answering one question, and the three layers stay
// distinct:
//
//   this file            — may this user enter a tenant app at all?
//   PermissionService    — which pages may they see? (page vocabulary)
//   functions/platform/userScope.js — which rows may they read? (data scope)
//
// Before CHA-552 the entry check compared the role against four literal
// names — ['admin', 'manager', 'user', 'viewer'] — which rejected every
// custom role AND three of the five seeded built-in roles (executive,
// sales, production), each of which `assignUserRole` happily assigns.
// A hardcoded vocabulary that nothing keeps in sync with the roles that
// actually exist can only drift in the direction of locking people out.
//
// Entry now depends on HOLDING a tenant-scoped role, not on its name.
// That is safe because the fields come from custom claims minted by
// `mintClaimsOnRoleAssignmentWrite` off `roleAssignments`, which is
// written only by gated callables — a non-empty roleId on the token
// already means an admin assigned it. What that role may then see is
// the job of the two layers below, not of this gate.

/**
 * Platform-global roles. These are not tenant-scoped and carry no
 * companyId, so they take their own branch.
 */
const PLATFORM_ROLES = ['platform_owner'];

/**
 * May this profile enter a tenant app?
 *
 * @param {object|null|undefined} userProfile
 * @param {string} [userProfile.role]      role id from the minted claim
 * @param {string} [userProfile.companyId] tenant id from the minted claim
 * @returns {boolean} strictly boolean — the pre-CHA-552 expression could
 *   return `undefined` or `''` when companyId was absent, which read as
 *   false everywhere it was used but is not the same value.
 */
export function canAccessTenant(userProfile) {
    if (!userProfile) return false;

    // Platform owner reaches any tenant and carries no companyId.
    if (PLATFORM_ROLES.includes(userProfile.role)) return true;

    return Boolean(userProfile.companyId) && Boolean(userProfile.role);
}

/**
 * Is this a platform-global role? Exported so callers can branch on it
 * without re-declaring the list.
 */
export function isPlatformRole(role) {
    return PLATFORM_ROLES.includes(role);
}
